import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

// Floating crypto icons for the hero section
export const FloatingElements = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Main glow effect */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] gradient-glow opacity-60" />

      {/* Floating coins */}
      <motion.div
        animate={{ y: [-10, 10, -10], rotate: [0, 5, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[15%] left-[10%]"
      >
        <div className="w-12 h-12 rounded-full gradient-gold shadow-lg flex items-center justify-center">
          <span className="text-accent-foreground font-bold text-lg">₿</span>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [10, -15, 10], rotate: [0, -5, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        className="absolute top-[20%] right-[12%]"
      >
        <div className="w-10 h-10 rounded-full bg-secondary shadow-lg flex items-center justify-center">
          <span className="text-secondary-foreground font-bold">Ξ</span>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [-8, 12, -8], rotate: [0, 8, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        className="absolute top-[35%] left-[5%]"
      >
        <div className="w-8 h-8 rounded-full bg-success shadow-lg flex items-center justify-center">
          <span className="text-success-foreground font-bold text-sm">$</span>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [5, -10, 5], rotate: [0, -3, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
        className="absolute top-[40%] right-[8%]"
      >
        <div className="w-9 h-9 rounded-full gradient-primary shadow-lg flex items-center justify-center">
          <span className="text-primary-foreground font-bold text-sm">◎</span>
        </div>
      </motion.div>

      {/* Sparkle effects */}
      <motion.div
        animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[25%] left-[25%] w-2 h-2 bg-primary rounded-full"
      />
      <motion.div
        animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
        className="absolute top-[30%] right-[20%] w-1.5 h-1.5 bg-accent rounded-full"
      />
      <motion.div
        animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
        className="absolute top-[45%] left-[15%] w-1 h-1 bg-secondary rounded-full"
      />

      {/* Decorative lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(330 85% 60%)" stopOpacity="0" />
            <stop offset="50%" stopColor="hsl(330 85% 60%)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="hsl(330 85% 60%)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.line
          x1="10%" y1="20%" x2="30%" y2="40%"
          stroke="url(#lineGrad)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
        />
        <motion.line
          x1="70%" y1="15%" x2="90%" y2="35%"
          stroke="url(#lineGrad)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.5, repeat: Infinity, repeatType: 'reverse', delay: 0.5 }}
        />
      </svg>
    </div>
  );
};

// Animated card illustration
export const CardIllustration = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
      className="relative"
    >
      {/* Card stack */}
      <motion.div
        animate={{ y: [-5, 5, -5], rotate: [-2, 2, -2] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        className="relative"
      >
        {/* Back card */}
        <div className="absolute -top-4 -left-4 w-72 h-44 rounded-2xl gradient-secondary opacity-50 transform rotate-[-8deg]" />

        {/* Middle card */}
        <div className="absolute -top-2 -left-2 w-72 h-44 rounded-2xl bg-muted opacity-70 transform rotate-[-4deg]" />

        {/* Front card - Main Blonix Card */}
        <div className="relative w-72 h-44 rounded-2xl gradient-primary shadow-glow overflow-hidden">
          {/* Card pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-foreground/10 -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-foreground/10 translate-y-1/2 -translate-x-1/4" />
          </div>

          {/* Card content */}
          <div className="relative p-5 h-full flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div className="w-10 h-7 rounded bg-accent/90 flex items-center justify-center">
                <div className="w-6 h-4 rounded-sm bg-accent-foreground/30" />
              </div>
              <div className="text-primary-foreground/90 font-display font-semibold text-lg">
                BLONIX
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-primary-foreground/80 font-mono text-sm tracking-widest">
                •••• •••• •••• 4289
              </div>
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-primary-foreground/60 text-xs">CARD HOLDER</div>
                  <div className="text-primary-foreground font-medium">JOHN DOE</div>
                </div>
                <div className="text-right">
                  <div className="text-primary-foreground/60 text-xs">EXPIRES</div>
                  <div className="text-primary-foreground font-medium">12/28</div>
                </div>
              </div>
            </div>
          </div>

          {/* Shimmer effect */}
          <motion.div
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', repeatDelay: 2 }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12"
          />
        </div>
      </motion.div>

      {/* NFC/Contactless icon */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute -bottom-3 -right-3 w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center shadow-lg"
      >
        <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6" strokeLinecap="round" />
          <path d="M11.5 17.5A5.5 5.5 0 0017 12c0-2.76-1-4-2-6-2.144-4.286-.448-8.108 4-12" strokeLinecap="round" />
        </svg>
      </motion.div>
    </motion.div>
  );
};

// Reward badge component
export const RewardBadge = ({ amount = '$10', currency = 'USDT' }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: -20 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl gradient-gold shadow-lg"
    >
      <div className="flex flex-col">
        <span className="text-accent-foreground/80 text-xs font-medium">{t('badge.text')}</span>
        <span className="text-accent-foreground font-display font-bold text-2xl">{t('badge.card')}</span>
      </div>

      {/* Animated coins */}
      <div className="relative">
        <motion.div
          animate={{ y: [-3, 3, -3], rotate: [-5, 5, -5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-10 h-10 rounded-full gradient-gold border-2 border-accent-foreground/20 shadow-md flex items-center justify-center"
        >
          <span className="text-accent-foreground font-bold">₮</span>
        </motion.div>
        <motion.div
          animate={{ y: [3, -3, 3], rotate: [5, -5, 5] }}
          transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
          className="absolute -top-1 -right-2 w-7 h-7 rounded-full gradient-gold border-2 border-accent-foreground/20 shadow-md flex items-center justify-center"
        >
          <span className="text-accent-foreground font-bold text-xs">$</span>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Step card component
export const StepCard = ({ number, title, description, icon, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.02, y: -5 }}
      className="relative p-5 rounded-2xl gradient-card border border-border/50 hover:border-primary/30 transition-all duration-300 group"
    >
      {/* Step number */}
      <div className="absolute -top-3 -left-2 w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-primary-foreground font-bold text-sm shadow-glow-sm">
        {number}
      </div>

      <div className="flex items-start gap-4 pt-2">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>

        <div className="flex-1">
          <h3 className="text-foreground font-display font-semibold text-base mb-1">{title}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Crypto icon row
export const CryptoIcons = () => {
  const cryptos = [
    { symbol: '₿', name: 'BTC', color: 'bg-amber-500' },
    { symbol: 'Ξ', name: 'ETH', color: 'bg-violet-500' },
    { symbol: '₮', name: 'USDT', color: 'bg-emerald-500' },
    { symbol: '◎', name: 'SOL', color: 'bg-gradient-to-r from-purple-500 to-cyan-400' },
  ];

  return (
    <div className="flex items-center gap-2">
      {cryptos.map((crypto, index) => (
        <motion.div
          key={crypto.name}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 * index }}
          whileHover={{ scale: 1.15, y: -2 }}
          className={`w-8 h-8 rounded-full ${crypto.color} flex items-center justify-center shadow-md cursor-pointer`}
          title={crypto.name}
        >
          <span className="text-white font-bold text-sm">{crypto.symbol}</span>
        </motion.div>
      ))}
      <span className="text-muted-foreground text-sm ml-1">+10 more</span>
    </div>
  );
};

// Payment method icons
export const PaymentIcons = () => {
  return (
    <div className="flex items-center gap-3">
      <motion.div
        whileHover={{ scale: 1.05 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span className="text-foreground text-xs font-medium">Pay</span>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.05 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="currentColor" d="M17.72 5.011H8.026c-2.22 0-4.044 1.607-4.044 3.604v6.897c0 1.997 1.824 3.604 4.044 3.604h9.693c2.22 0 4.044-1.607 4.044-3.604V8.615c0-1.997-1.824-3.604-4.044-3.604z" />
        </svg>
        <span className="text-foreground text-xs font-medium">Apple Pay</span>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.05 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border"
      >
        <div className="flex">
          <div className="w-4 h-4 rounded-full bg-red-500 -mr-1.5" />
          <div className="w-4 h-4 rounded-full bg-amber-400" />
        </div>
        <span className="text-foreground text-xs font-medium">Card</span>
      </motion.div>
    </div>
  );
};
